import { DEFAULT_IMAGE_DELIVERY, imageSource, imageUrl } from '../image-url.js'
import { imageCandidateWidths } from '../image-policy.js'

const states = new WeakMap()

/**
 * Native video.poster and CSS backgrounds have no srcset. Assign their URL
 * after layout and visibility are known, without changing playback or refs.
 */
function createImageDirective(kind) {
  return {
    mounted(element, binding) {
      const config = binding.instance.$.appContext.provides.imageDelivery ?? DEFAULT_IMAGE_DELIVERY
      const state = { source: imageSource(binding.value, config), config, visible: false, width: 0, requested_width: 0, url: '', resize: null, intersection: null }
      const candidates = imageCandidateWidths(binding.modifiers.hero ? 2560 : binding.modifiers.thumb ? 320 : 960)
      const assign = () => {
        if (!state.visible || state.width <= 0 || state.source.url === '') return
        const target = state.width * Math.min(window.devicePixelRatio, 2)
        const width = candidates.find(value => value >= target) ?? candidates.at(-1)
        // Retain the larger source when layout shrinks; only upgrade in-place.
        if (width <= state.requested_width) return
        const url = imageUrl(state.source, width)
        if (url === state.url) return
        state.requested_width = width
        state.url = url
        if (kind === 'poster') element.poster = url
        else element.style.setProperty('--media-image', `url(${JSON.stringify(url)})`)
      }
      state.assign = assign
      state.resize = new window.ResizeObserver(entries => {
        state.width = entries[0].contentRect.width
        assign()
      })
      state.intersection = new window.IntersectionObserver(entries => {
        state.visible = entries[0].isIntersecting
        assign()
      }, { rootMargin: binding.modifiers.eager ? '0px' : '160px' })
      state.resize.observe(element)
      state.intersection.observe(element)
      states.set(element, state)
    },
    updated(element, binding) {
      if (binding.value === binding.oldValue) return
      const state = states.get(element)
      state.source = imageSource(binding.value, state.config)
      state.url = ''
      state.requested_width = 0
      if (kind === 'poster') element.removeAttribute('poster')
      else element.style.removeProperty('--media-image')
      state.assign()
    },
    beforeUnmount(element) {
      const state = states.get(element)
      state.resize.disconnect()
      state.intersection.disconnect()
      states.delete(element)
    },
  }
}

export const vMediaPoster = createImageDirective('poster')
export const vMediaBackground = createImageDirective('background')
